/**
 * COSTING.1d — shared recorded-vs-estimated rendering for report cost cells.
 *
 * Per the design paper (E1/E4): recorded rows render plain; estimated rows in
 * text.secondary with the dotted-tooltip idiom; uncosted rows show "—" with a
 * tooltip. The estimate/recorded decision is keyed off the row's costBasis, NOT
 * off whether a stamped cost is present — pre-costing and dormant-venue rows
 * both lack stamps for different reasons, and the once-only mixed-basis caption
 * (keyed to the venue's activation date) explains the difference.
 */

export type CostDisplayKind = 'recorded' | 'estimated' | 'uncosted';

export interface CostCell {
  kind: CostDisplayKind;
  /** Cost to show (minor units); null → render "—". */
  cost: number | null;
  /** Dotted-tooltip text for estimated/uncosted; null for plain recorded. */
  tooltip: string | null;
}

/**
 * Decide how a report cost cell renders.
 * @param costBasis the movement/row's stamped basis, or null when absent
 * @param recorded  the stamped cost (minor units) where present
 * @param current   the CURRENT_LAST_COST fallback (minor units)
 */
export function costBasisCell(
  costBasis: string | null | undefined,
  recorded: number | null | undefined,
  current: number | null | undefined,
): CostCell {
  switch (costBasis) {
    case 'ACTUAL':
      return { kind: 'recorded', cost: recorded ?? null, tooltip: null };
    case 'GENESIS':
      return { kind: 'estimated', cost: recorded ?? null, tooltip: 'Estimated — carried from the opening-balance snapshot when cost tracking started.' };
    case 'FALLBACK':
      return { kind: 'estimated', cost: recorded ?? null, tooltip: 'Estimated — no purchase cost was recorded, so the last known cost was used.' };
    case 'UNCOSTED':
      return { kind: 'uncosted', cost: null, tooltip: 'No cost was recorded for this stock.' };
    default:
      // Absent basis: a pre-costing row, or a dormant venue → current-cost estimate.
      return { kind: 'estimated', cost: current ?? null, tooltip: 'Estimated at current cost (recorded before cost tracking started, or this venue is not yet tracking cost).' };
  }
}

/** Does a set of rows mix recorded and estimated bases? (drives the once-only caption) */
export function hasMixedBasis(rows: Array<{ costBasis?: string | null }>): boolean {
  let recorded = false, estimated = false;
  for (const r of rows) {
    if (r.costBasis === 'ACTUAL') recorded = true;
    else estimated = true; // GENESIS/FALLBACK/UNCOSTED/absent all read as non-plain
    if (recorded && estimated) return true;
  }
  return false;
}
